import { Table } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import {
	QUES_CODE_PREFIX,
	formatBrsrNumericCellValue,
	renderQuestionAnswer,
	renderType1TableAnswerForCurrentYear,
	renderType3TableAnswer,
	renderType3TableAnswerForCurrentYear
} from '../../../../core/utilities/BRSRUtils';
import './../Reporting.scss';

const BRSRPrinciple2 = ({
	t,
	page4,
	yearFilter
}) => {

	// Wrapper to maintain compatibility with existing calls
	const renderTableAnswerForCurrentYearWrapper = (qIndex, colNos) => {
		const block = page4[QUES_CODE_PREFIX + qIndex];
		if (block?.keyValueType === 'TYPE_3_TABLE') {
			const type3 = renderType3TableAnswerForCurrentYear(page4, qIndex, colNos);
			if (type3 != null) return type3;
		}
		return renderType1TableAnswerForCurrentYear(page4, 4, qIndex, colNos);
	};

	const renderP2Q1 = () => {
		const currentYearAnswer = {};
		const previousYearAnswer = {};
		if (page4['currentYear']) {
			page4['currentYear'][QUES_CODE_PREFIX + 'P2-1']?.keyValues?.forEach(val => {
				currentYearAnswer[val.key] = formatBrsrNumericCellValue(val.value);
			});
		}
		if (page4['previousYear']) {
			page4['previousYear'][QUES_CODE_PREFIX + 'P2-1']?.keyValues?.forEach(val => {
				previousYearAnswer[val.key] = formatBrsrNumericCellValue(val.value);
			});
		}
		return (
			<div className='brsr-list-item'>
				<b>1. Percentage of R&D and capital expenditure (capex) investments in specific technologies to improve the environmental and social impacts of product and processes to total R&D and capex investments made by the entity, respectively.</b>
				<div className="mt-2">
					<Table bordered size="sm">
						<thead>
							<tr>
								<th></th>
								<th>{yearFilter}</th>
								<th>{yearFilter - 1} </th>
								<th>Details of improvements in environmental and social impacts</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>R&D</td>
								<td>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-1aa'] ?? ''}</td>
								<td>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-1aa'] ?? ''}</td>
								<td>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-1ac'] ?? ''}</td>
							</tr>
							<tr>
								<td>Capex</td>
								<td>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-1ba'] ?? ''}</td>
								<td>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-1ba'] ?? ''}</td>
								<td>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-1bc'] ?? ''}</td>
							</tr>
						</tbody>
					</Table>
				</div>
			</div>
		);
	};

	const renderP2Q2 = () => {
		const currentYearAnswer = {};
		if (page4['currentYear']) {
			page4['currentYear'][QUES_CODE_PREFIX + 'P2-2']?.keyValues?.forEach(val => {
				currentYearAnswer[val.key] = formatBrsrNumericCellValue(val.value);
			});
		}
		return (
			<div className='brsr-list-item'>
				<b>2. a. Does the entity have procedures in place for sustainable sourcing? (Yes/No)</b>
				<div className='brsr-sublist'>
					{currentYearAnswer[QUES_CODE_PREFIX + 'P2-2a'] ?? ''}
				</div>
				<b style={{ marginLeft: '17px' }}>b. If yes, what percentage of inputs were sourced sustainably?</b>
				<div className='brsr-sublist'>
					{currentYearAnswer[QUES_CODE_PREFIX + 'P2-2b'] ?? ''}
				</div>
			</div>
		);
	};

	const renderP2Q3 = () => {
		return (
			renderQuestionAnswer('3. Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for (a) Plastics (including packaging) (b) E-waste (c) Hazardous waste and (d) other waste.', page4['currentYear']?.[QUES_CODE_PREFIX + 'P2-3']?.keyValues?.find(val => val.key === QUES_CODE_PREFIX + 'P2-3a')?.value ?? '')
		);
	};

	const renderP2Q4 = () => {
		return (
			renderQuestionAnswer('4. Whether Extended Producer Responsibility (EPR) is applicable to the entity\'s activities (Yes / No). If yes, whether the waste collection plan is in line with the Extended Producer Responsibility (EPR) plan submitted to Pollution Control Boards? If not, provide steps taken to address the same.', page4['currentYear']?.[QUES_CODE_PREFIX + 'P2-4']?.keyValues?.find(val => val.key === QUES_CODE_PREFIX + 'P2-4a')?.value ?? '')
		);
	};

	const renderP2LI3 = () => {
		const questionKey = QUES_CODE_PREFIX + 'P2-LI3';
		const parseCell = (kv) => {
			const cellNo = parseInt(String(kv?.cell ?? '').trim(), 10);
			return Number.isFinite(cellNo) ? cellNo : null;
		};
		const getCellMap = (block, suffix) => {
			const map = {};
			block?.keyValues?.forEach((kv) => {
				if (kv.key !== `${questionKey}${suffix}`) return;
				const cellNo = parseCell(kv);
				if (cellNo == null) return;
				map[cellNo] = formatBrsrNumericCellValue(kv.value);
			});
			return map;
		};

		const currentYearBlock = page4?.currentYear?.[questionKey];
		const previousYearBlock = page4?.previousYear?.[questionKey];
		const currentYearMaterialByCell = getCellMap(currentYearBlock, 'a');
		const previousYearMaterialByCell = getCellMap(previousYearBlock, 'a');
		const currentYearValueByCell = getCellMap(currentYearBlock, 'b');
		const previousYearValueByCell = getCellMap(previousYearBlock, 'b');
		const rowCells = [...new Set([
			...Object.keys(currentYearMaterialByCell),
			...Object.keys(previousYearMaterialByCell),
			...Object.keys(currentYearValueByCell),
			...Object.keys(previousYearValueByCell)
		])]
			.map(Number)
			.filter((n) => Number.isFinite(n))
			.sort((a, b) => a - b);
		return (
			<div className='brsr-list-item'>
				<b>3. Percentage of recycled or reused input material to total material (by value) used in production (for manufacturing industry) or providing services (for service industry).</b>
				<div className="mt-2">
					<Table bordered size="sm">
						<thead>
							<tr>
								<th rowSpan="2">Indicate input material</th>
								<th colSpan="2">Recycled or re-used input material to total material</th>
							</tr>
							<tr>
								<th className='FY_class white-nowrap'>FY {yearFilter}</th>
								<th className='white-nowrap'>FY {yearFilter - 1}</th>
							</tr>
						</thead>
						<tbody>
							{rowCells.map((cellNo) => (
								<tr key={`P2-LI3-row-${cellNo}`}>
									<td>{currentYearMaterialByCell[cellNo] ?? previousYearMaterialByCell[cellNo] ?? ''}</td>
									<td className='FY_class_content'>{currentYearValueByCell[cellNo] ?? ''}</td>
									<td className='text-right'>{previousYearValueByCell[cellNo] ?? ''}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</div>
			</div>
		);
	};

	const renderP2LI4 = () => {
		const currentYearAnswer = {};
		const previousYearAnswer = {};
		if (page4['currentYear']) {
			page4['currentYear'][QUES_CODE_PREFIX + 'P2-LI4']?.keyValues?.forEach(val => {
				currentYearAnswer[val.key] = formatBrsrNumericCellValue(val.value);
			});
		}
		if (page4['previousYear']) {
			page4['previousYear'][QUES_CODE_PREFIX + 'P2-LI4']?.keyValues?.forEach(val => {
				previousYearAnswer[val.key] = formatBrsrNumericCellValue(val.value);
			});
		}
		return (
			<div className='brsr-list-item'>
				<b>4. Of the products and packaging reclaimed at end of life of products, amount (in metric tonnes) reused, recycled, and safely disposed, as per the following format:</b>
				<div className="mt-2">
					<Table bordered size="sm">
						<thead>
							<tr>
								<th rowSpan="2"></th>
								<th colSpan="3" className='FY_class white-nowrap'>FY {yearFilter}</th>
								<th colSpan="3" className='white-nowrap'>FY {yearFilter - 1}</th>
							</tr>
							<tr>
								<th className='FY_class_sub'>Re-Used</th>
								<th className='FY_class_sub'>Recycled</th>
								<th className='FY_class_sub'>Safely Disposed</th>
								<th>Re-Used</th>
								<th>Recycled</th>
								<th>Safely Disposed</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Plastics (including packaging )</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4aa'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ab'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ac'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4aa'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ab'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ac'] ?? ''}</td>
							</tr>
							<tr>
								<td>E-waste</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ba'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4bb'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4bc'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ba'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4bb'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4bc'] ?? ''}</td>
							</tr>
							<tr>
								<td>Hazardous waste</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ca'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4cb'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4cc'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4ca'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4cb'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4cc'] ?? ''}</td>
							</tr>
							<tr>
								<td>Other waste</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4da'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4db'] ?? ''}</td>
								<td className='FY_class_content text-right'>{currentYearAnswer[QUES_CODE_PREFIX + 'P2-LI4dc'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4da'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4db'] ?? ''}</td>
								<td className='text-right'>{previousYearAnswer[QUES_CODE_PREFIX + 'P2-LI4dc'] ?? ''}</td>
							</tr>
						</tbody>
					</Table>
				</div>
			</div>
		);
	};

	return (
		<section id="PRINCIPLE_2" className="Main_Section PRINCIPLE_2">
			<div className="ETA_card_Section_wrapper">
				<div className="ETA_card_Section 1">
					<div className="ETA_card_Section_head mb-3">
						<h5>PRINCIPLE 2 Businesses should provide goods and services in a manner that is sustainable and safe</h5>
					</div>
					<div className="ETA_card_Section_sub">
						<section className="mb-4">
							<div className="ETA_card_Section_sub_head mb-3">
								<b>Essential Indicators</b>
							</div>
							<div className="ETA_card_Section_sub_body">
								<div className='brsr-list'>
									{renderP2Q1()}
									{renderP2Q2()}
									{renderP2Q3()}
									{renderP2Q4()}
								</div>
							</div>
							<div className="ETA_card_Section_sub_head mt-4 mb-3">
								<b>Leadership Indicators </b>
							</div>
							<div className="ETA_card_Section_sub_body">
								<div className='brsr-list'>
									<div className='brsr-list-item'>
										<b>1. Has the entity conducted Life Cycle Perspective / Assessments (LCA) for any of its products (for manufacturing industry) or for its services (for service industry)? If yes, provide details in the following format?</b>
										<div className="mt-2">
											<Table bordered size="sm">
												<thead>
													<tr>
														<th>NIC Code</th>
														<th>Name of Product /Service</th>
														<th>% of total Turnover contributed </th>
														<th>Boundary for which the Life Cycle Perspective / Assessment was conducted</th>
														<th>Whether conducted by independent external agency (Yes/No)</th>
														<th>Results communicated in public domain (Yes/No) If yes, provide the web-link.</th>
													</tr>
												</thead>
												<tbody>
													{renderTableAnswerForCurrentYearWrapper('P2-LI1', ['a', 'b', 'c', 'd', 'e', 'f'])}
												</tbody>
											</Table>
										</div>
									</div>
									<div className='brsr-list-item'>
										<b>2. If there are any significant social or environmental concerns and/or risks arising from production or disposal of your products / services, as identified in the Life Cycle Perspective / Assessments (LCA) or through any other means, briefly describe the same along-with action taken to mitigate the same.</b>
										<div className="mt-2">
											<Table bordered size="sm">
												<thead>
													<tr>
														<th>Name of Product / Service</th>
														<th>Description of the risk / concern</th>
														<th>Action Taken </th>
													</tr>
												</thead>
												<tbody>
													{renderTableAnswerForCurrentYearWrapper('P2-LI2', ['a', 'b', 'c'])}
												</tbody>
											</Table>
										</div>
									</div>
									{renderP2LI3()}
									{renderP2LI4()}
									<div className='brsr-list-item'>
										<b>5. Reclaimed products and their packaging materials (as percentage of products sold) for each product category.</b>
										<div className="mt-2">
											<Table bordered size="sm">
												<thead>
													<tr>
														<th>Indicate product category</th>
														<th>Reclaimed products and their packaging materials as % of total products sold in respective category</th>
													</tr>
												</thead>
												<tbody>
													{renderTableAnswerForCurrentYearWrapper('P2-LI5', ['a', 'b'])}
												</tbody>
											</Table>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</section>
	);
};

export default withTranslation()(BRSRPrinciple2);
